import { motion } from "framer-motion";
import { Check, X, Minus, BarChart3 } from "lucide-react";

const mechanisms = [
  {
    name: "Static Rules Engine",
    latency: "< 1ms",
    determinism: "full",
    context: "none",
    evasion: "low",
    adaptability: "none",
    highlight: false,
  },
  {
    name: "LLM-as-Judge",
    latency: "200ms — 14s",
    determinism: "low",
    context: "high",
    evasion: "low",
    adaptability: "high",
    highlight: false,
  },
  {
    name: "Agent-as-Judge",
    latency: "14s — 900s",
    determinism: "very low",
    context: "high",
    evasion: "very low",
    adaptability: "high",
    highlight: false,
  },
  {
    name: "LeapForce (GBT + IF)",
    latency: "< 1ms",
    determinism: "full",
    context: "high",
    evasion: "high",
    adaptability: "high",
    highlight: true,
  },
];

const rows = [
  {
    label: "Inference Latency",
    key: "latency",
  },
  {
    label: "Determinism",
    key: "determinism",
    render: (v) => {
      if (v === "full") return <Check size={14} className="text-[#10B981]" />;
      if (v === "low" || v === "very low") return <X size={14} className="text-[#EF4444]" />;
      return <Minus size={14} className="text-[#F59E0B]" />;
    },
  },
  {
    label: "Context Awareness",
    key: "context",
    render: (v) => {
      if (v === "high") return <Check size={14} className="text-[#10B981]" />;
      if (v === "none") return <X size={14} className="text-[#EF4444]" />;
      return <Minus size={14} className="text-[#F59E0B]" />;
    },
  },
  {
    label: "Evasion Resistance",
    key: "evasion",
    render: (v) => {
      if (v === "high") return <Check size={14} className="text-[#10B981]" />;
      if (v === "low" || v === "very low") return <X size={14} className="text-[#EF4444]" />;
      return <Minus size={14} className="text-[#F59E0B]" />;
    },
  },
  {
    label: "Adaptability",
    key: "adaptability",
    render: (v) => {
      if (v === "high") return <Check size={14} className="text-[#10B981]" />;
      if (v === "none") return <X size={14} className="text-[#EF4444]" />;
      return <Minus size={14} className="text-[#F59E0B]" />;
    },
  },
];

export default function ComparisonTable() {
  return (
    <section
      id="compare"
      data-testid="comparison-section"
      className="relative py-24 lg:py-32 bg-[#0D0F14] border-y border-[#1A2332]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <span className="text-xs font-mono text-[#00E5FF] uppercase tracking-[0.2em] flex items-center gap-2 mb-4">
            <BarChart3 size={14} />
            Comparison
          </span>
          <h2
            data-testid="comparison-headline"
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4"
          >
            Why Not <span className="text-[#94A3B8]">LLM-as-Judge?</span>
          </h2>
          <p className="text-base text-[#94A3B8] max-w-2xl leading-relaxed">
            Enterprise compliance demands deterministic, auditable security decisions. 
            Probabilistic models cannot provide this guarantee.
          </p>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="overflow-x-auto"
        >
          <div className="min-w-[700px] border border-[#1A2332]">
            {/* Header */}
            <div className="grid grid-cols-5 border-b border-[#1A2332]">
              <div className="p-4 bg-[#0D0F14]">
                <span className="text-[10px] font-mono text-[#475569] uppercase tracking-[0.2em]">
                  Metric
                </span>
              </div>
              {mechanisms.map((m) => (
                <div
                  key={m.name}
                  data-testid={`comparison-col-${m.name.toLowerCase().replace(/[\s()\/+]/g, "-")}`}
                  className={`p-4 text-center ${
                    m.highlight
                      ? "bg-[#00E5FF]/5 border-x border-[#00E5FF]/20"
                      : "bg-[#0D0F14]"
                  }`}
                >
                  <span
                    className={`text-xs font-mono font-bold ${
                      m.highlight ? "text-[#00E5FF]" : "text-white"
                    }`}
                  >
                    {m.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Rows */}
            {rows.map((row) => (
              <div
                key={row.key}
                className="grid grid-cols-5 border-b border-[#1A2332] last:border-b-0"
              >
                <div className="p-4 bg-[#050508] flex items-center">
                  <span className="text-xs font-mono text-[#94A3B8]">
                    {row.label}
                  </span>
                </div>
                {mechanisms.map((m) => (
                  <div
                    key={`${row.key}-${m.name}`}
                    className={`p-4 flex items-center justify-center ${
                      m.highlight
                        ? "bg-[#00E5FF]/5 border-x border-[#00E5FF]/20"
                        : "bg-[#050508]"
                    }`}
                  >
                    {row.render ? (
                      row.render(m[row.key])
                    ) : (
                      <span
                        className={`text-xs font-mono ${
                          m.highlight ? "text-[#00E5FF] font-bold" : "text-[#94A3B8]"
                        }`}
                      >
                        {m[row.key]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
